import * as React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from 'recharts';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { HardDrive, RefreshCw, Database } from 'lucide-react';
import { useDbStats } from '../../hooks/admin/useDbStats';
import type { TableStats } from '../../hooks/admin/types/database';

interface StorageBreakdownChartProps {
  className?: string;
  limit?: number;
  showLegend?: boolean;
  minSizeBytes?: number;
}

interface StorageSegment {
  name: string;
  value: number;
  percentage: number;
  formattedSize: string;
  color: string;
}

const COLORS = [
  '#3b82f6', // blue-500
  '#8b5cf6', // violet-500
  '#ec4899', // pink-500
  '#f59e0b', // amber-500
  '#10b981', // emerald-500
  '#06b6d4', // cyan-500
  '#f97316', // orange-500
  '#a855f7', // purple-500
  '#14b8a6', // teal-500
  '#64748b', // slate-500
];

/**
 * Format bytes to human-readable string
 */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
}

/**
 * StorageBreakdownChart component - visualizes database storage by table
 * Uses Recharts PieChart to show storage distribution across database tables
 *
 * @param className - Optional CSS class name
 * @param limit - Maximum number of tables to show (default: 10, others grouped as "Other")
 * @param showLegend - Whether to show the legend (default: true)
 * @param minSizeBytes - Minimum size in bytes for a table to be shown separately (default: 0)
 */
export function StorageBreakdownChart({
  className,
  limit = 10,
  showLegend = true,
  minSizeBytes = 0,
}: StorageBreakdownChartProps) {
  const { stats, isLoading, isError, mutate } = useDbStats();

  // Process table data into chart segments
  const storageSegments = React.useMemo((): StorageSegment[] => {
    if (!stats?.tables || stats.tables.length === 0) return [];

    // Sort tables by total size descending
    const sortedTables = [...stats.tables].sort(
      (a, b) => b.total_size_bytes - a.total_size_bytes
    );

    // Filter by minimum size
    const filteredTables = sortedTables.filter(
      (table) => table.total_size_bytes >= minSizeBytes
    );

    if (filteredTables.length === 0) return [];

    const totalSize = stats.total_db_size_bytes;

    // Take top N tables, group the rest
    const topTables = filteredTables.slice(0, limit);
    const otherTables = filteredTables.slice(limit);

    const segments: StorageSegment[] = topTables.map((table, index) => ({
      name: table.table_name,
      value: table.total_size_bytes,
      percentage: (table.total_size_bytes / totalSize) * 100,
      formattedSize: table.total_size,
      color: COLORS[index % COLORS.length],
    }));

    // Add "Other" segment if there are more tables
    if (otherTables.length > 0) {
      const otherSize = otherTables.reduce(
        (sum, table) => sum + table.total_size_bytes,
        0
      );
      segments.push({
        name: `Other (${otherTables.length} tables)`,
        value: otherSize,
        percentage: (otherSize / totalSize) * 100,
        formattedSize: formatBytes(otherSize),
        color: '#94a3b8', // slate-400
      });
    }

    return segments;
  }, [stats, limit, minSizeBytes]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload as StorageSegment;
      return (
        <div
          className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3 shadow-lg"
        >
          <p className="font-semibold text-slate-900 dark:text-slate-100 mb-1">
            {data.name}
          </p>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Size: <span className="font-medium text-slate-900 dark:text-slate-100">{data.formattedSize}</span>
          </p>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Percentage: <span className="font-medium text-slate-900 dark:text-slate-100">{data.percentage.toFixed(2)}%</span>
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom legend
  const renderLegend = (props: any) => {
    const { payload } = props;
    return (
      <div className="flex flex-wrap gap-3 justify-center mt-4">
        {payload.map((entry: any, index: number) => (
          <div key={`legend-${index}`} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-xs text-slate-600 dark:text-slate-400">
              {entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <Card className={cn('', className)} role="status" aria-label="Loading storage breakdown">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>
            <Skeleton className="h-10 w-10 rounded-xl" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[300px]">
            <Skeleton className="h-[250px] w-[250px] rounded-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (isError) {
    return (
      <Card className={cn('border-red-200 dark:border-red-800', className)}>
        <CardHeader>
          <CardTitle className="text-red-600 dark:text-red-400 flex items-center gap-2">
            <HardDrive className="h-5 w-5" />
            Storage Breakdown
          </CardTitle>
          <CardDescription className="text-red-500 dark:text-red-400">
            Failed to load storage data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            There was an error loading the storage breakdown chart.
          </p>
          <button
            onClick={() => mutate()}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Retry
          </button>
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (!stats || storageSegments.length === 0) {
    return (
      <Card className={cn('', className)}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="h-5 w-5" />
            Storage Breakdown
          </CardTitle>
          <CardDescription>Database storage by table</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center h-[300px] text-center">
            <Database className="h-12 w-12 text-slate-300 dark:text-slate-600 mb-3" />
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-1">
              No storage data available
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              There are no tables to display or all tables are below the minimum size threshold.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Success state with data
  return (
    <Card className={cn('', className)} role="region" aria-label="Storage breakdown chart">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <HardDrive className="h-5 w-5" />
              Storage Breakdown
            </CardTitle>
            <CardDescription>
              Total: {stats.total_db_size} across {stats.tables.length} tables
            </CardDescription>
          </div>
          <button
            onClick={() => mutate()}
            className="p-2 text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            aria-label="Refresh storage data"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Chart */}
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={storageSegments}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={100}
              fill="#8884d8"
              dataKey="value"
              label={({ name, percentage }) =>
                percentage > 5 ? `${name}: ${percentage.toFixed(1)}%` : ''
              }
            >
              {storageSegments.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            {showLegend && <Legend content={renderLegend} />}
          </PieChart>
        </ResponsiveContainer>

        {/* Summary table */}
        <div className="mt-6 space-y-2">
          <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">
            Breakdown Details
          </h4>
          <div className="space-y-1">
            {storageSegments.map((segment, index) => (
              <div
                key={`detail-${index}`}
                className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-3 h-3 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: segment.color }}
                  />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {segment.name}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="secondary" className="text-xs">
                    {segment.percentage.toFixed(2)}%
                  </Badge>
                  <span className="text-sm text-slate-600 dark:text-slate-400 min-w-[80px] text-right">
                    {segment.formattedSize}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
